import { useMemo, useState } from "react";
import { Checkbox, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import LoadingState from "../../../components/LoadingState/index.js";
import EmptyState from "../../../components/EmptyState/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import {
  accountRemediationService,
  type OrphanGroup,
} from "../../../api/accountRemediation.service.js";
import { filterOutBlankGroups, ORPHANED_SUBMISSIONS_COLUMN_HEADERS } from "./remediation.js";
import styles from "./AccountRemediation.module.css";

function firmRefLabel(firmRef: string | null): string {
  return firmRef === null || firmRef === "" ? "blank/null" : firmRef;
}

function OrphanedSubmissionsTab() {
  const { data, loading, error, refetch } = useQuery(
    () => accountRemediationService.getOrphanSubmissions(),
    [],
  );

  const [hideBlankGroups, setHideBlankGroups] = useState(false);

  const orphans = data?.orphans ?? [];
  const visibleOrphans = hideBlankGroups ? filterOutBlankGroups(orphans) : orphans;

  const columns: Column<OrphanGroup>[] = useMemo(
    () => [
      {
        header: ORPHANED_SUBMISSIONS_COLUMN_HEADERS[0],
        accessor: "firmRef" as keyof OrphanGroup,
        render: (value: unknown) => {
          const firmRef = value as string | null;
          const blank = firmRef === null || firmRef === "";
          return (
            <span className={blank ? styles.blankRef : undefined}>{firmRefLabel(firmRef)}</span>
          );
        },
      },
      {
        header: ORPHANED_SUBMISSIONS_COLUMN_HEADERS[1],
        accessor: "count" as keyof OrphanGroup,
        render: (value: unknown) => String(value),
      },
      {
        header: ORPHANED_SUBMISSIONS_COLUMN_HEADERS[2],
        accessor: "class" as keyof OrphanGroup,
        render: (value: unknown) => String(value),
      },
    ],
    [],
  );

  const status = (error as (Error & { status?: number }) | null)?.status;
  const isAuthError = status === 401 || status === 403;

  if (isAuthError) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.content}>
      {loading && <LoadingState message="Loading orphaned submissions…" />}

      {error && !isAuthError && (
        <EmptyState
          variant="error"
          title="Failed to load orphaned submissions"
          message={error.message}
          action={{ label: "Retry", onClick: refetch }}
        />
      )}

      {!loading && !error && orphans.length === 0 && (
        <EmptyState
          variant="empty"
          title="No orphaned submissions found"
          message="There are no orphaned submission groups."
        />
      )}

      {!loading && !error && orphans.length > 0 && (
        <>
          <div className={styles.toolbar}>
            <Checkbox
              id="hide-blank-groups"
              label="Hide blank/null groups"
              aria-label="Hide blank/null groups"
              checked={hideBlankGroups}
              onChange={event => setHideBlankGroups(event.target.checked)}
            />
          </div>

          <div className={styles.tableContainer}>
            <DataTable
              data={visibleOrphans}
              columns={columns}
              rowKey="firmRef"
              size="small"
              emptyMessage="No groups match the current filter."
            />
          </div>
        </>
      )}
    </div>
  );
}

export default OrphanedSubmissionsTab;
